import React, { useState } from 'react';
import AppImage from '@/components/AppImage/AppImage';
import BookingErrorModal from '@/components/BookingModal/BookingErrorModal';
import { MISSING_DATES_ERROR, type BookingErrorCopy } from '@/lib/booking/bookingErrors';
import logoOrangeIcon from '@/assets/logo-orange-no-beta.svg';
import {
  PLACE_STAY_INLINE_ALLEZ_PROVIDERS,
  type Stay22AllezProvider,
} from '@/lib/booking/stay22AllezProviders';
import type { PlaceModalSearchParams } from '@/components/CombinedContentPlaceModal/PlaceModalBookingWidget';

export interface PlaceStayBookingOptionsProps {
  isLoggedIn: boolean;
  isNuiteeLoading: boolean;
  isStay22Loading: boolean;
  getBookingParams: () => PlaceModalSearchParams | null;
  onSelectNuitee: (params: PlaceModalSearchParams) => void;
  onSelectStay22Vendor: (provider: Stay22AllezProvider, params: PlaceModalSearchParams) => void;
  onSignInClick: () => void;
  /** Hide the "Compare rates" heading when the host card already has its own title. */
  showTitle?: boolean;
  /** OTA rows (Booking.com, Expedia, …) open via Stay22; hide them when Stay22 is off. */
  showVendors?: boolean;
}

const VENDOR_LOGOS: Partial<
  Record<
    Stay22AllezProvider,
    { src: string; alt: string; width: number; height: number; className?: string }
  >
> = {
  booking: {
    src: '/images/stay22/booking-com-logo.png',
    alt: 'Booking.com',
    width: 118,
    height: 20,
    className: 'h-5 w-auto max-w-[135px] object-contain object-left',
  },
  expedia: {
    src: '/images/stay22/expedia-logo.svg',
    alt: 'Expedia',
    width: 101,
    height: 20,
    className: 'h-5 w-auto max-w-[105px] object-contain object-left',
  },
  hotelscom: {
    src: '/images/stay22/hotels-com-logo.png',
    alt: 'Hotels.com',
    width: 100,
    height: 24,
    className: 'h-6 w-auto max-w-[110px] object-contain object-left',
  },
  vrbo: {
    src: '/images/stay22/vrbo-logo.svg',
    alt: 'Vrbo',
    width: 100,
    height: 32,
    className: 'h-6 w-auto max-w-[110px] object-contain object-left',
  },
};

/** Mobile: Figma 44px-tall "Book" / "Sign In" buttons. */
const bookButtonClassName =
  'shrink-0 rounded-xl bg-[#252528] px-4 py-2 font-manrope text-sm font-normal text-white shadow-[0px_0px_20px_0px_rgba(0,0,0,0.15)] transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 max-md:h-11 max-md:leading-5 max-md:tracking-[0.5px]';

const gtActionButtonClassName =
  'shrink-0 rounded-xl bg-[#FF5212] px-4 py-2 font-manrope text-sm font-normal text-white shadow-[0px_0px_20px_0px_rgba(0,0,0,0.15)] transition hover:bg-[#e64910] disabled:cursor-not-allowed disabled:opacity-50 max-md:h-11 max-md:leading-5 max-md:tracking-[0.5px]';

/** The chosen dates and guests, or null until both dates are picked. */
function resolveBookingParams(
  getBookingParams: () => PlaceModalSearchParams | null,
): PlaceModalSearchParams | null {
  const params = getBookingParams();
  return params?.checkIn && params.checkOut ? params : null;
}

/**
 * Stay booking vendor chooser (place modal + Check Availability sheet).
 * Layout matches Figma: GT Exclusive first, then OTA logo rows with Book CTAs.
 */
const PlaceStayBookingOptions: React.FC<PlaceStayBookingOptionsProps> = ({
  isLoggedIn,
  isNuiteeLoading,
  isStay22Loading,
  getBookingParams,
  onSelectNuitee,
  onSelectStay22Vendor,
  onSignInClick,
  showTitle = true,
  showVendors = true,
}) => {
  const [bookingError, setBookingError] = useState<BookingErrorCopy | null>(null);

  const handleStay22Vendor = (provider: Stay22AllezProvider) => {
    const params = resolveBookingParams(getBookingParams);
    if (!params) {
      setBookingError(MISSING_DATES_ERROR);
      return;
    }
    onSelectStay22Vendor(provider, params);
  };

  const handleNuitee = () => {
    const params = resolveBookingParams(getBookingParams);
    if (!params) {
      setBookingError(MISSING_DATES_ERROR);
      return;
    }
    onSelectNuitee(params);
  };

  return (
    <div className="flex flex-col gap-3" data-testid="place-stay-booking-options">
      <BookingErrorModal error={bookingError} onClose={() => setBookingError(null)} />
      {showTitle && (
        <h3 className="font-manrope text-base font-semibold leading-[22px] tracking-[0.02em] text-[#252528]">
          Compare rates
        </h3>
      )}

      <ul className="flex flex-col gap-2 max-md:gap-3" role="list">
        <li>
          <div className="flex items-center justify-between gap-3 rounded-xl border border-[#E1E1E7] bg-[#FFDBC6] p-3 max-md:border-transparent">
            <div className="flex min-w-0 flex-col items-start gap-0.5">
              <AppImage
                src={logoOrangeIcon}
                alt="Globe Thrivers"
                width={140}
                height={21}
                className="h-[21px] w-auto max-w-[140px] object-contain object-left"
              />
              <span className="font-manrope text-[10px] leading-tight text-black max-md:text-[9px] max-md:leading-3">
                Access exclusive hotel rates
              </span>
              <span className="sr-only">GT Exclusive</span>
            </div>
            {isLoggedIn ? (
              <button
                type="button"
                onClick={handleNuitee}
                disabled={isNuiteeLoading}
                className={gtActionButtonClassName}
                data-testid="place-stay-book-nuitee"
              >
                {isNuiteeLoading ? 'Opening…' : 'Book'}
              </button>
            ) : (
              <button
                type="button"
                onClick={onSignInClick}
                className={gtActionButtonClassName}
                data-testid="place-stay-book-nuitee-sign-in"
              >
                Sign In
              </button>
            )}
          </div>
        </li>

        {showVendors && PLACE_STAY_INLINE_ALLEZ_PROVIDERS.map(({ provider, label }) => {
          const logo = VENDOR_LOGOS[provider];
          return (
            <li key={provider}>
              <div className="flex items-center justify-between gap-3 rounded-xl border border-[#E1E1E7] bg-white p-3">
                <div className="flex min-w-0 items-center gap-3">
                  {logo ? (
                    <AppImage
                      src={logo.src}
                      alt={logo.alt}
                      width={logo.width}
                      height={logo.height}
                      className={logo.className}
                    />
                  ) : (
                    <span className="font-manrope text-base font-semibold tracking-tight text-[#006EDB]">
                      {label}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleStay22Vendor(provider)}
                  disabled={isStay22Loading}
                  className={bookButtonClassName}
                  data-testid={`place-stay-book-vendor-${provider}`}
                >
                  {isStay22Loading ? 'Opening…' : 'Book'}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PlaceStayBookingOptions;
